/**
 * CENTRALIZED UI LABELS
 * ─────────────────────
 * Change any label here to update it everywhere in the app.
 * Organize by: entities → statuses → actions → toast messages → UI strings.
 */

/* ── Entity names ─────────────────────────────────────────────────────────── */
export const ENTITIES = {
  pledge:        { singular: 'Offering',      plural: 'Offerings'      },
  receipt:       { singular: 'Receipt',       plural: 'Receipts'       },
  voucher:       { singular: 'Payment',       plural: 'Payments'       },
  journalEntry:  { singular: 'Journal Entry', plural: 'Journal Entries' },
  category:      { singular: 'Category',      plural: 'Categories'     },
  family:        { singular: 'Family',        plural: 'Families'       },
  member:        { singular: 'Member',        plural: 'Members'        },
  derasar:       { singular: 'Derasar',       plural: 'Derasars'       },
  financialYear: { singular: 'Financial Year', plural: 'Financial Years' },
  ledger:        { singular: 'Ledger',        plural: 'Ledgers'        },
  event:         { singular: 'Event',         plural: 'Events'         },
  donation:      { singular: 'Donation',      plural: 'Donations'      },
  guestMember:   { singular: 'Guest',         plural: 'Guests'         },
  expenseType:   { singular: 'Expense Type',  plural: 'Expense Types'  },
};

/* ── Status display labels ────────────────────────────────────────────────── */
export const STATUS = {
  pending:    'Pending',
  approved:   'Approved',
  rejected:   'Rejected',
  cancelled:  'Cancelled',
  completed:  'Completed',
  partial:    'Partial',
  active:     'Active',
  inactive:   'Inactive',
  deactivated:'Deactivated',
  open:       'Open',
  closed:     'Closed',
  draft:      'Draft',
  locked:     'Locked',
  frozen:     'Frozen',
  expiring:   'Expiring',
  expired:    'Expired',
  you:        'You',
};

/* ── Receipt / donation types ─────────────────────────────────────────────── */
export const RECEIPT_TYPES = {
  cash:      'Cash / UPI',
  gold:      'Gold',
  anonymous: 'Shah A/C (Walk-in)',
  bhandar:   'Bhandar',
};

/* ── Payment modes ────────────────────────────────────────────────────────── */
export const PAYMENT_MODES = {
  cash:   'Cash',
  cheque: 'Cheque',
  upi:    'UPI',
  neft:   'NEFT / RTGS',
  dd:     'Demand Draft',
};

/* ── Action button labels ─────────────────────────────────────────────────── */
export const ACTIONS = {
  new:     (entity) => `New ${entity.singular}`,
  add:     (entity) => `Add ${entity.singular}`,
  create:  (entity) => `Create ${entity.singular}`,
  edit:    'Edit',
  delete:  'Delete',
  cancel:  'Cancel',
  save:    'Save',
  saving:  'Saving…',
  creating:'Creating…',
  back:    'Back',
  close:   'Close',
  approve: 'Approve',
  reject:  'Reject',
  confirm: 'Confirm',
  download:'Download PDF',
  email:   'Send Email',
  showAll: 'Show All',
  pendingOnly: 'Pending Only',
  leave:   'Leave',
  remove:  'Remove',
  reactivate: 'Reactivate',
  clearFilters: 'Clear Filters',
};

/* ── Toast / notification messages ───────────────────────────────────────── */
export const TOAST = {
  loadFailed:     (entity) => `Failed to load ${entity.plural.toLowerCase()}`,
  createSuccess:  (entity) => `${entity.singular} created`,
  updateSuccess:  (entity) => `${entity.singular} updated`,
  deleteSuccess:  (entity) => `${entity.singular} deleted`,
  cancelSuccess:  (entity) => `${entity.singular} cancelled`,
  approveSuccess: (entity) => `${entity.singular} approved`,
  rejectSuccess:  (entity) => `${entity.singular} rejected`,
  leaveSuccess:   (entity) => `Left ${entity.singular.toLowerCase()} successfully`,
  removeSuccess:  (entity) => `${entity.singular} removed`,
  reactivateSuccess: (entity) => `${entity.singular} reactivated`,
  emailSent:      'Email sent successfully',
  pdfFailed:      'Failed to generate PDF',
  emailFailed:    'Failed to send email',
  actionFailed:   (msg) => msg || 'Action failed',
  reasonRequired: 'Cancellation reason is required',
  fieldRequired:  (field) => `${field} is required`,
  selectRequired: (field) => `Please select a ${field}`,
};

/* ── Form labels / hints ──────────────────────────────────────────────────── */
export const FORM = {
  optional:            '(optional)',
  withinFinancialYear: '(within financial year)',
  financialYear:       'Financial Year',
  selectYear:          'Select year…',
  notes:               'Notes',
  notesPlaceholder:    'Any remarks…',
  reason:              'Reason (required)',
  reasonPlaceholder:   'Enter reason for cancellation…',
  date:                'Date',
  name:                'Name',
  search:              (entity) => `Search ${entity.plural.toLowerCase()}…`,
};

/* ── Filter bar labels ────────────────────────────────────────────────────── */
export const FILTERS = {
  allYears:      'All Years',
  allStatuses:   'All Statuses',
  allCategories: 'All Categories',
  allTypes:      'All Types',
  allMembers:    'All Members',
  donorName:     'Member Name',
  searchPlaceholder: 'Search…',
};

/* ── Table / column header labels ────────────────────────────────────────── */
export const TABLE = {
  member:        'Member',
  category:      'Category',
  year:          'Year',
  date:          'Date',
  amount:        'Amount (₹)',
  paid:          'Paid',
  outstanding:   'Outstanding',
  status:        'Status',
  actions:       'Actions',
  totals:        'Totals',
  receiptNo:     'Receipt No',
  voucherNo:     'Voucher No',
  narration:     'Narration',
  pledgeDate:    (entity) => `${entity.singular} Date`,
  noResults:     (entity) => `No ${entity.plural.toLowerCase()} found.`,
};

/* ── Summary card labels ──────────────────────────────────────────────────── */
export const SUMMARY = {
  totalOffered:   'Total Offered',
  collected:      'Collected',
  outstanding:    'Outstanding',
  totalReceived:  'Total Received',
  receiptsCount:  'Receipts Count',
  goldDonations:  'Gold Donations',
  totalPaid:      'Total Paid',
  totalVouchers:  'Total Vouchers',
};

/* ── Modal labels ─────────────────────────────────────────────────────────── */
export const MODAL = {
  cancel: {
    title:             (entity) => `Cancel ${entity.singular}`,
    journalEntryNote:  'A reversal journal entry will be created automatically.',
    reasonLabel:       'Reason (required)',
    reasonPlaceholder: 'Enter reason for cancellation…',
    confirm:           'Confirm Cancel',
    back:              'Back',
  },
  confirm: {
    back:    'Back',
    confirm: 'Confirm',
  },
};

/* ── Empty / placeholder states ───────────────────────────────────────────── */
export const EMPTY = {
  noData:        (entity) => `No ${entity.plural.toLowerCase()} found.`,
  createFirst:   (entity) => `No ${entity.plural.toLowerCase()} yet. Create one to get started.`,
  selectDerasar: 'Select a Derasar to continue.',
  noMembers:     'No members available.',
  noResults:     'No results found.',
  noCategories:  'No categories.',
};

/* ── Navigation / page headings ───────────────────────────────────────────── */
export const NAV = {
  dashboard:        'Dashboard',
  pledges:          'Offerings',
  receipts:         'Receipts',
  paymentVouchers:  'Payments',
  journalEntries:   'Journal Entries',
  financialYears:   'Financial Years',
  openingBalance:   'Opening Balance',
  chartOfAccounts:  'Chart of Accounts',
  ledger:           'Ledger',
  reports:          'Reports',
  categories:       'Donation Categories',
  family:           'My Family',
  users:            'Members',
  derasar:          'My Derasar',
  browse:           'Browse Derasars',
  profile:          'Profile',
  settings:         'Settings',
  superAdmin:       'Super Admin',
};

/* ── Misc helper: pluralize dynamically ───────────────────────────────────── */
export const pluralize = (entity, count) =>
  count === 1 ? entity.singular : entity.plural;
